// Phase A4's wiring for main.ts (gates A4.1 and A4.2): the saves (src/save/) and the screens (src/ui/), joined: the
// UI asks the player about sync conflicts and lists the slots; the saves autosave on the flow's zone changes. One
// call, so main.ts stays a list of modules.
import type { WebGLRenderer } from "three";
import type { Rebinding } from "@rpg/runtime";
import type { ScreenRender } from "@rpg/ui";
import type { SpikeBoot } from "./boot";
import type { SpikeContent } from "./content";
import type { FaultGroup } from "./faults";
import { mountSaves, SAVE_FAULTS, SAVE_FAULT_NAMES } from "./save";
import type { SimMount } from "./sim";
import { mountUi, UI_FAULT_NAMES } from "./ui";

/** Mount the saves and the UI. Returns the flow's screens, the handle keys and the planted-defect groups. */
export function mountSaveUi(d: { content: SpikeContent; sim: SimMount["handle"]; renderer: WebGLRenderer; boot: SpikeBoot; stepHz: number; rebinding: Rebinding; screens?: Record<string, ScreenRender> }) {
  const tuning = (id: string): number => {
    const row = d.content.registry.get("tuning", id);
    if (!row) throw new Error(`no tuning value ${id}`);
    return row.value;
  };
  const saves = mountSaves({
    sim: d.sim,
    stepHz: d.stepHz,
    onFlow: (fn) => void d.boot.flow.on(fn),
  });
  const ui = mountUi({
    content: d.content,
    saves,
    renderer: d.renderer,
    phone: () => d.boot.report().platform.kind === "phone",
    deviceTier: () => d.boot.report().tier,
    pauseIfPlaying: () => d.boot.flow.pause(),
    writeDelayMs: tuning("save.settingsWriteDelay") * 1000,
    rebinding: d.rebinding,
    ...(d.screens ? { screens: d.screens } : {}),
  });
  const saveGroup: FaultGroup = {
    names: SAVE_FAULT_NAMES,
    get: (n) => (n === "phase-d-slices-kept" ? SAVE_FAULTS.phaseDSlicesKept : SAVE_FAULTS.noSaveMigration),
    set: (n, on) => void (n === "phase-d-slices-kept" ? (SAVE_FAULTS.phaseDSlicesKept = on) : (SAVE_FAULTS.noSaveMigration = on)),
  };
  return {
    /** Phase D1 and D2: the UI itself (the rpg layer's, accessibility's and the map's screens join its stack), and a
     *  scene's screens and pause entries added to it (the rpg layer's). */
    ui: ui.ui,
    saves,
    flowScreens: ui.flowScreens,
    settings: ui.settings,
    addScreens: ui.addScreens,
    handle: { saves: saves.handle, ...ui.handle },
    faultGroups: [saveGroup, ui.faultGroup],
    faultNames: [...SAVE_FAULT_NAMES, ...UI_FAULT_NAMES],
  };
}
